import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Campaign,
  CampaignPreview,
  CommsAllowance,
  CommsChannel,
  CommsKind,
  CommsMessage,
  CommsTemplate,
  CreateCampaign,
  ListCommsMessagesQuery,
  UpdateCommsPolicy,
  UpsertCommsTemplate,
} from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS8 — the comms surface. The allowance meter + at-cap policy (top of CommsPage) were the Phase-2
 * contract; this module adds the Phase-3 SEND pipeline: campaigns (draft → preview → launch), the
 * per-message audit log, and template management. TanStack Query over the single typed client,
 * mirroring data/vouchers.ts / data/billing.ts.
 *
 * MONEY HONESTY (non-negotiable): a campaign's recipientCount / estimatedCostPence / wouldPauseAtCap
 * and a sent campaign's costPence are SERVER figures. The composer NEVER computes cost client-side —
 * it POSTs a draft, asks the server for a dry-run PREVIEW, and only then offers Launch. These reads are
 * money-adjacent and DELIBERATELY NOT in PERSISTED_PREFIXES (see lib/query.ts) — a spend estimate is
 * never served from an offline replica.
 *
 * IDEMPOTENCY (precise): CREATE is genuinely idempotent on a client-minted (salonId, idempotencyKey)
 * — a retried Create returns the original draft and inserts nothing new. LAUNCH idempotency is
 * PER-CAMPAIGN, not key-based: the server guards on campaign status (a 'sending'/'sent' campaign is a
 * no-op) and the fan-out job de-dupes on a stable jobId (campaign:<id>). The send body still carries an
 * idempotencyKey for forward-compatibility but the launch route currently IGNORES it — the per-campaign
 * guard is what makes a double-tap safe, alongside the button's `isPending` gate. Templates upsert by
 * key (manager-only on the API). 422/409 surface through ApiClientError.
 */

export const CAMPAIGNS_PAGE = 50;
export const MESSAGES_PAGE = 50;

interface Page<T> {
  data: T[];
  pagination: { limit: number; offset: number; total: number };
}
export type CampaignPage = Page<Campaign>;
export type CommsMessagePage = Page<CommsMessage>;

// ── Allowance + policy (Phase-2 contract — unchanged) ───────────────────────────────

export function useCommsAllowance() {
  return useQuery({
    queryKey: qk.comms,
    queryFn: async (): Promise<CommsAllowance> => unwrap(await api.GET('/v1/comms/allowance')),
  });
}

export function useUpdateCommsPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateCommsPolicy) =>
      unwrap(await api.PUT('/v1/comms/policy', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.comms }),
  });
}

// ── Campaigns ───────────────────────────────────────────────────────────────────────

/**
 * NOTE — useCampaigns / useCampaign are currently UNMOUNTED (nothing renders a campaign list/detail
 * yet; the composer reuses the freshly-created draft in place). They are kept because
 * useCreateCampaign / useSendCampaign already invalidate their query keys, so wiring a list view later
 * is a drop-in. CAVEAT: there is no GET-by-id endpoint, so useCampaign(id) fetches the FIRST page
 * (CAMPAIGNS_PAGE = 50) and .find()s — it would silently miss an id beyond the first page on a
 * >50-campaign salon. Replace the list-find with a real /comms/campaigns/{id} route before mounting.
 */

/** Manager-only, paginated, status-filtered list of campaigns (newest first per the API). */
export function useCampaigns(status?: string, offset = 0) {
  return useQuery({
    queryKey: qk.campaigns(status ?? null, offset),
    staleTime: 0,
    queryFn: async (): Promise<CampaignPage> =>
      unwrap(
        await api.GET('/v1/comms/campaigns', {
          params: {
            query: {
              status: status as never,
              limit: CAMPAIGNS_PAGE,
              offset,
            },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/** One campaign (the detail/send view). Gated on an id so an empty selection doesn't fire. */
export function useCampaign(id: string | null) {
  return useQuery({
    queryKey: id ? qk.campaign(id) : ['campaign', 'none'],
    enabled: Boolean(id),
    staleTime: 0,
    queryFn: async (): Promise<Campaign> => {
      // The list returns Campaign[]; there is no GET-by-id endpoint, so we read the list and find it.
      // The detail flow re-uses the freshly-created draft from useCreateCampaign instead in practice.
      const page = unwrap(
        await api.GET('/v1/comms/campaigns', { params: { query: { limit: CAMPAIGNS_PAGE } } }),
      );
      const found = page.data.find((c) => c.id === id);
      if (!found) throw new Error('Campaign not found');
      return found;
    },
  });
}

/**
 * Create a DRAFT campaign. Idempotent on idempotencyKey: the caller mints the key once per intent
 * (see CampaignComposer) so a retried Create returns the original draft and creates nothing new.
 * Server re-validates the body; we cast `body as never` (the typed-client Input shape is structurally
 * the same as CreateCampaign).
 */
export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateCampaign): Promise<Campaign> =>
      // server re-validates
      unwrap(await api.POST('/v1/comms/campaigns', { body: body as never })),
    onSuccess: (campaign) => {
      qc.setQueryData(qk.campaign(campaign.id), campaign);
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
}

/**
 * Dry-run a draft: resolve audience, estimate cost/segments/consent skips and cap impact. This is the
 * bulk-spend SAFETY surface — the composer must render the returned recipientCount / estimatedCostPence
 * / wouldPauseAtCap BEFORE it enables Launch. No body; the server reads the stored draft.
 */
export function usePreviewCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<CampaignPreview> =>
      unwrap(await api.POST('/v1/comms/campaigns/{id}/preview', { params: { path: { id } } })),
    onSuccess: (preview, id) => {
      qc.setQueryData(qk.campaignPreview(id), preview);
    },
  });
}

/**
 * Launch a campaign (202): enqueues the consent-aware fan-out — never auto-spends past the cap. Launch
 * is idempotent PER-CAMPAIGN on the server (status guard + stable campaign:<id> job de-dupe), NOT on the
 * supplied idempotencyKey — the server accepts the key but currently ignores it. We still pass one and
 * gate the button on isPending so a double-tap is a safe no-op. Returns the updated Campaign.
 */
export function useSendCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; idempotencyKey: string }): Promise<Campaign> =>
      // server re-validates
      unwrap(
        await api.POST('/v1/comms/campaigns/{id}/send', {
          params: { path: { id: input.id } },
          body: { idempotencyKey: input.idempotencyKey } as never,
        }),
      ),
    onSuccess: (campaign) => {
      qc.setQueryData(qk.campaign(campaign.id), campaign);
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
      void qc.invalidateQueries({ queryKey: ['comms-messages'] });
      void qc.invalidateQueries({ queryKey: qk.comms });
    },
  });
}

// ── Message audit log ─────────────────────────────────────────────────────────────

export interface CommsMessagesFilter {
  channel?: CommsChannel;
  kind?: CommsKind;
  clientId?: string;
  appointmentId?: string;
  campaignId?: string;
  from?: string;
  to?: string;
}

/**
 * The per-message audit log (recipients redacted by the API — render what it returns). Paged, with the
 * whole filter object in the key so a filter change caches independently. keepPreviousData so paging
 * doesn't blank the table.
 */
export function useCommsMessages(filter: CommsMessagesFilter = {}, offset = 0) {
  return useQuery({
    queryKey: qk.commsMessages(filter as Record<string, unknown>, offset),
    staleTime: 0,
    queryFn: async (): Promise<CommsMessagePage> =>
      unwrap(
        await api.GET('/v1/comms/messages', {
          params: {
            query: {
              ...filter,
              limit: MESSAGES_PAGE,
              offset,
            } as ListCommsMessagesQuery,
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

// ── Templates ─────────────────────────────────────────────────────────────────────

/** The template list (optionally filtered by kind/channel). Returns a bare array (not paginated). */
export function useCommsTemplates(kind?: CommsKind, channel?: CommsChannel) {
  return useQuery({
    queryKey: qk.commsTemplates(kind ?? null, channel ?? null),
    queryFn: async (): Promise<CommsTemplate[]> =>
      unwrap(
        await api.GET('/v1/comms/templates', {
          params: { query: { kind, channel } },
        }),
      ),
  });
}

/** Upsert a template by key (manager-only on the API). Server re-validates the body. */
export function useUpsertTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { key: string; body: UpsertCommsTemplate }): Promise<CommsTemplate> =>
      // server re-validates
      unwrap(
        await api.PUT('/v1/comms/templates/{key}', {
          params: { path: { key: input.key } },
          body: input.body as never,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comms-templates'] }),
  });
}
